import { definedMap } from "@glideapps/ts-necessities";
import type {
    AppDescriptionContext,
    ComponentBuilder,
    ComponentDescriptor,
    PropertyDescriptor,
    WireInflationBackend,
    WireRowComponentHydrationBackend,
    WireRowComponentHydrator,
} from ".";
import { asString } from "../../components/component-helpers";
import type { PrimitiveValue } from "../../components/computation-types";
import type { SerializedComponent } from "../../components/SerializedApp";
import { AppKind } from "../../components/SerializedApp";
import { isNotEmpty, isPrimitiveValue } from "../../computation-model/data";
import { assert, undefinedIfEmptyString } from "../../Support";
import type { WireBaseComponent, WireFieldComponentBase } from "../../wire/base-components";
import { isBound } from "../../wire/types";
import {
    getDefaultValuesForComponent,
    makeNumber,
    makeText,
    makeValidationFlagForProperty,
    setColumnInRowForEditComponent,
} from "../builder-utils";
import type {
    ColumnTypeKind,
    ComponentKind,
    DefaultableComponentDescription,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    TableColumn,
    TableGlideType,
} from "../description";
import { getTableColumnDisplayName, isPrimitiveType, makeEmptyComponentDescription } from "../description";
import type { SerializedElementBuilder } from "../serialized-component";
import {
    makeDefaultValuePropertyDescriptor,
    makePlaceholderPropertyDescriptor,
    makeTextPropertyDescriptor,
} from "./descriptor-utils";
import { ComponentHandlerBase } from "./handler";
import type { ColumnPropertyHandler } from "./property-handlers";
import { isRequiredPropertyHandler } from "./property-handlers";
import { PropertySection } from "./property-sections";
import {
    getColumnProperty,
    getStringProperty,
    makeColumnProperty,
    makeStringProperty,
    makeSwitchProperty,
} from "./property-utils";

export interface BaseFieldComponentDescription extends DefaultableComponentDescription {
    readonly placeholder: PropertyDescription;
    readonly title: PropertyDescription;
    readonly isRequired?: PropertyDescription;

    // only used in the text field
    readonly minLength?: PropertyDescription;
    readonly maxLength?: PropertyDescription;
}

export abstract class BaseFieldComponentHandler<
    T extends BaseFieldComponentDescription
> extends ComponentHandlerBase<T> {
    // `_fieldType` is used for the default value.  If it's `undefined`, default
    // values are not supported.
    constructor(componentKind: string, private readonly _fieldType: ColumnTypeKind | undefined) {
        super(componentKind);
    }

    public getIsEditor(): boolean {
        return true;
    }

    public needValidation(desc: T): boolean {
        return isRequiredPropertyHandler.getSwitch(desc);
    }

    protected fieldEntryForTableColumn(
        builder: ComponentBuilder,
        desc: T,
        outputTable: TableGlideType,
        outputContext: SerializedComponent,
        mutatingScreenKind: MutatingScreenKind | undefined,
        valueName: string,
        valuePropertyHandler: ColumnPropertyHandler<T>,
        uiName: string,
        validFlag?: SerializedComponent,
        converterBuiltin?: string,
        extraArgs?: Record<string, SerializedComponent | undefined>
    ): SerializedComponent {
        const { b } = builder;

        const propertyName = valuePropertyHandler.getColumnName(desc);
        if (propertyName === undefined) return b.emptyUI;

        const setColumnsResult = setColumnInRowForEditComponent(
            b,
            mutatingScreenKind,
            propertyName,
            b.getProperty(propertyName, outputContext, outputTable),
            undefined,
            outputTable,
            undefined,
            b.getContext
        );
        if (setColumnsResult === undefined) return b.emptyUI;
        const { lets, valueToModify, action, isEnabled } = setColumnsResult;

        const actions: SerializedComponent[] = [];
        if (action !== undefined) {
            actions.push(action);
        }

        const value = converterBuiltin === undefined ? valueToModify : b.getVariable("converted");

        const isRequired = isRequiredPropertyHandler.getSwitch(desc);

        const minLength = definedMap(desc.minLength, v => makeNumber(builder, v, outputTable));
        const maxLength = definedMap(desc.maxLength, v => makeNumber(builder, v, outputTable));

        const args: { [name: string]: SerializedComponent | undefined } = {
            placeholder: makeText(builder, desc.placeholder, outputTable) ?? b.constant(""),
            title: makeText(builder, desc.title, outputTable) ?? b.constant(""),
            onChange: actions.length > 0 ? b.sequence(actions) : undefined,
            isEnabled,
            minLength,
            maxLength,
            ...extraArgs,
        };
        args[valueName] = value;
        args.isRequired = b.constant(isRequired);
        if (validFlag !== undefined) {
            args.validFlag = validFlag;
        }

        let ui = b.ui(uiName, args);
        if (converterBuiltin !== undefined) {
            ui = b.let([...lets, ["converted", b.builtinCall(converterBuiltin, [valueToModify])]], ui);
        } else {
            ui = b.let(lets, ui);
        }
        return ui;
    }

    public makeValidationFlag(
        b: SerializedElementBuilder,
        desc: T,
        outputContext: SerializedComponent,
        table: TableGlideType
    ): SerializedComponent | undefined {
        return makeValidationFlagForProperty(b, outputContext, getColumnProperty(desc.propertyName), table);
    }

    protected getFieldDescriptor(
        ccc: AppDescriptionContext,
        name: string,
        description: string,
        group: string,
        img: string,
        propertyNamePropertyHandler: ColumnPropertyHandler<T>,
        isLegacy: boolean,
        helpUrl: string,
        mutatingScreenKind: MutatingScreenKind | undefined,
        defaultCaption: string,
        additionalPropertyDescriptors?: PropertyDescriptor[]
    ): ComponentDescriptor<T> {
        const properties: PropertyDescriptor[] = [propertyNamePropertyHandler];
        if (this._fieldType !== undefined && ccc.appKind === AppKind.App) {
            const defaultValuePropertyDescriptor = makeDefaultValuePropertyDescriptor(
                ccc,
                mutatingScreenKind,
                this._fieldType
            );
            if (defaultValuePropertyDescriptor !== undefined) {
                properties.push(defaultValuePropertyDescriptor);
            }
        }
        properties.push(
            makeTextPropertyDescriptor("title", "Title", "Title", true, mutatingScreenKind, {
                isCaption: defaultCaption,
                propertySection: PropertySection.Design,
                searchable: false,
            }),
            makePlaceholderPropertyDescriptor(mutatingScreenKind, PropertySection.Design)
        );
        if (mutatingScreenKind !== undefined) {
            properties.push(isRequiredPropertyHandler);
        }
        properties.push(...(additionalPropertyDescriptors ?? []), ...this.getBasePropertyDescriptors());
        return {
            name,
            description,
            img,
            group: ccc.appKind === AppKind.Page ? "Form Elements" : group,
            helpUrl,
            isLegacy,
            properties,
        };
    }

    public getDescriptiveName(desc: T, tables: InputOutputTables, ccc: AppDescriptionContext): [string, string] {
        const names = super.getDescriptiveName(desc, tables, ccc);
        const fieldTitle = getStringProperty(desc.title);
        return [names[0], undefinedIfEmptyString(fieldTitle) ?? names[1]];
    }

    public getDefaultValues(
        builder: ComponentBuilder,
        desc: T,
        { input }: InputOutputTables
    ): readonly [string, SerializedComponent][] {
        return getDefaultValuesForComponent(builder, desc, input, this._fieldType);
    }

    protected inflateComponent(
        ib: WireInflationBackend,
        desc: T,
        finish: (
            base: WireFieldComponentBase,
            value: PrimitiveValue,
            token: string,
            hb: WireRowComponentHydrationBackend
        ) => WireBaseComponent,
        validate?: (value: PrimitiveValue, hb: WireRowComponentHydrationBackend) => boolean
    ): WireRowComponentHydrator | undefined {
        const propertyName = getColumnProperty(desc.propertyName);
        if (propertyName === undefined) return undefined;

        const [titleGetter] = ib.getValueGetterForProperty(desc.title, true);
        const [placeholderGetter] = ib.getValueGetterForProperty(desc.placeholder, true);
        const [valueGetter] = ib.getValueGetterForProperty(desc.propertyName, false, { inOutputRow: true });

        const isRequired = isRequiredPropertyHandler.getSwitch(desc);

        return hb => {
            const title = titleGetter(hb);
            const placeholder = placeholderGetter(hb);

            let value = valueGetter(hb);
            if (!isPrimitiveValue(value)) {
                value = undefined;
            }

            const token = hb.registerOnValueChange(propertyName);
            if (token === undefined) return undefined;

            const isValid = validate?.(value, hb) !== false;

            return [
                finish(
                    {
                        id: desc.componentID,
                        title: asString(title),
                        placeholder: asString(placeholder),
                        isRequired,
                    },
                    value,
                    token,
                    hb
                ),
                // We're doing something ugly here, because we haven't fixed
                // ##isRequiredVsIsValid yet.  Basically, if the validator
                // tells us we're not valid, we pretend that we require a
                // value and we don't have it.
                isRequired || !isValid,
                isBound(value) && isNotEmpty(value) && isValid,
            ];
        };
    }
}

export function defaultFieldComponent(column: TableColumn, kind: ComponentKind): BaseFieldComponentDescription {
    assert(isPrimitiveType(column.type));
    return {
        ...makeEmptyComponentDescription(kind),
        propertyName: makeColumnProperty(column.name),
        defaultValue: undefined,
        placeholder: makeStringProperty(""),
        title: makeStringProperty(getTableColumnDisplayName(column)),
        isRequired: makeSwitchProperty(false),
    };
}
